import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { signOut, type User } from "firebase/auth";
import type { Messaging } from "firebase/messaging";
import {
  collection,
  deleteDoc,
  deleteField,
  doc,
  getDoc,
  getDocs,
  query,
  updateDoc,
  where,
} from "firebase/firestore";
import useAppUser from "../hooks/useAppUser";
import { requestNotificationPermission } from "../utils/permissions";
import { initFirebaseMessaging } from "../services/firebase";
import saveFcmToken from "../features/users/helpers/saveFcmToken";
import { detectPlatformForFcm } from "../services/api/notificationService";
import { setSentryUser, addBreadcrumb } from "../sentry.config";
import { setAnalyticsUserId, setUserProperties, logLogout, logNotificationPermission } from "../analytics.config";
import APP_CONFIG from "../app.config";
import { auth, db, getMessagingInstance } from '../services/firebase';
import { clearE2EAuthState, isE2EMockAuthEnabled } from "../utils/e2eAuth";

type Capabilities = {
  notifications: boolean;
};

type UserContextValue = {
  user: User | null;
  role: string | null;
  uid: string | null;
  capabilities: Capabilities;
  loading: boolean;
  logout: () => Promise<void>;
};

type UserProviderProps = {
  children: ReactNode;
};

type StoredFcmToken = {
  token?: string;
  platform?: string;
  deviceId?: string;
  userAgent?: string;
  createdAt?: string;
  updatedAt?: string;
};

type UserDocShape = {
  fcmTokens?: Array<StoredFcmToken | string>;
  fcmToken?: string;
};

const UserContext = createContext<UserContextValue | undefined>(undefined);

export const UserProvider = ({ children }: UserProviderProps) => {
  const { firebaseUser, role, uid, loading: loadingAppUser } = useAppUser();

  const [capabilities, setCapabilities] = useState<Capabilities>({
    notifications: false,
  });

  const [hasRegisteredToken, setHasRegisteredToken] = useState<boolean>(false);
  const [currentFcmToken, setCurrentFcmToken] = useState<string | null>(null);

  const buildFcmTokenId = (token: string | null): string | null => {
    if (!token) return null;
    return token.replace(/[^a-zA-Z0-9_-]/g, "_").slice(0, 400);
  };

  const removeFcmTokenFromFirestore = async (userId: string, token: string): Promise<void> => {
    if (!userId || !token) return;

    try {
      const userRef = doc(db, "users", userId);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        const userData = (userSnap.data() as UserDocShape) ?? {};
        const existingTokens = Array.isArray(userData.fcmTokens) ? userData.fcmTokens : [];
        const filteredTokens = existingTokens.filter((item) => {
          if (!item) return false;
          if (typeof item === "string") return item !== token;
          return item.token !== token;
        });

        const shouldUpdateArray = filteredTokens.length !== existingTokens.length;
        const shouldDeleteLegacyField = userData.fcmToken === token;

        if (shouldUpdateArray || shouldDeleteLegacyField) {
          const updates: Record<string, unknown> = {
            fcmTokens: filteredTokens,
          };

          if (shouldDeleteLegacyField) {
            updates.fcmToken = deleteField();
          }

          await updateDoc(userRef, updates);
        }
      }
    } catch (error) {
      console.error("Erreur lors de la suppression du token FCM (source canonique users/{uid}.fcmTokens):", error);
    }

    try {
      const tokenId = buildFcmTokenId(token);
      if (tokenId) {
        await deleteDoc(doc(db, "users", userId, "fcmTokens", tokenId));
      }
    } catch (error) {
      console.error(
        "Erreur lors de la suppression du token FCM (subcollection users/{uid}/fcmTokens/{tokenId}):",
        error
      );
    }

    try {
      const tokenQuery = query(collection(db, "users", userId, "fcmTokens"), where("token", "==", token));
      const tokenSnapshot = await getDocs(tokenQuery);
      await Promise.all(tokenSnapshot.docs.map((tokenDoc) => deleteDoc(tokenDoc.ref)));
    } catch (error) {
      console.error("Erreur lors du fallback de suppression du token FCM:", error);
    }
  };

  const getCurrentMessagingToken = async (messaging: Messaging): Promise<string | null> => {
    try {
      const { getToken } = await import("firebase/messaging");

      let serviceWorkerRegistration: ServiceWorkerRegistration | undefined;
      try {
        serviceWorkerRegistration = await navigator.serviceWorker.ready;
      } catch {
        serviceWorkerRegistration = undefined;
      }

      return await getToken(messaging, {
        vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY,
        ...(serviceWorkerRegistration ? { serviceWorkerRegistration } : {}),
      });
    } catch (error) {
      console.error("Erreur lors de la récupération du token FCM courant:", error);
      return null;
    }
  };

  const logout = async (): Promise<void> => {
    console.log("Logout initiated...");
    if (isE2EMockAuthEnabled()) {
      clearE2EAuthState();
      setHasRegisteredToken(false);
      setCurrentFcmToken(null);
      return;
    }

    const currentUid = uid;

    // Define the cleanup task
    const cleanupMessaging = async () => {
      try {
        console.log("Cleaning up FCM tokens before logout...");
        const messaging = await getMessagingInstance();

        if (messaging && currentUid) {
          let tokenToDelete = currentFcmToken;

          if (!tokenToDelete) {
            tokenToDelete = await getCurrentMessagingToken(messaging);
          }

          if (tokenToDelete) {
            console.log("Removing token from Firestore...");
            await removeFcmTokenFromFirestore(currentUid, tokenToDelete);
            
            try {
              const { deleteToken } = await import("firebase/messaging");
              await deleteToken(messaging);
              console.log("FCM logical token deleted.");
            } catch (error) {
              console.warn("deleteToken(messaging) failed (safe to ignore):", error);
            }
          }
        }
      } catch (error) {
        console.error("Erreur lors du nettoyage FCM au logout:", error);
      }
    };

    // Race the cleanup against a 3-second timeout to ensure the user isn't stuck
    try {
      await Promise.race([
        cleanupMessaging(),
        new Promise((resolve) => setTimeout(() => {
          console.warn("FCM cleanup timed out (3s), proceeding with sign-out.");
          resolve(null);
        }, 3000))
      ]);
    } catch (error) {
      console.error("Unexpected error during logout preparation:", error);
    }

    // Always perform the actual Firebase sign-out
    try {
      console.log("Calling Firebase signOut...");
      await signOut(auth);
      console.log("Successfully signed out from Firebase");
    } catch (error) {
      console.error("Erreur lors du signOut Firebase:", error);
    } finally {
      // Clear local state regardless of sign-out success
      setHasRegisteredToken(false);
      setCurrentFcmToken(null);
    }
  };

  useEffect(() => {
    if (firebaseUser && uid && role) {
      setSentryUser({
        uid,
        email: firebaseUser.email,
        role,
      });
      addBreadcrumb("User logged in", "user", {
        uid,
        role,
      });

      setAnalyticsUserId(uid);
      setUserProperties({
        role,
        email: firebaseUser.email,
      });
    } else {
      setSentryUser(null as unknown as object);
      setAnalyticsUserId(null);

      if (!firebaseUser) {
        addBreadcrumb("User logged out", "user");
        logLogout();
      }
    }
  }, [firebaseUser, uid, role]);

  useEffect(() => {
    const registerToken = async () => {
      if (firebaseUser && uid && !hasRegisteredToken) {
        const token = await initFirebaseMessaging();
        if (token) {
          const platform = detectPlatformForFcm();
          await saveFcmToken(uid, token, platform);
          setCurrentFcmToken(token);
          setHasRegisteredToken(true);
        }
      }
    };

    void registerToken();
  }, [firebaseUser, uid, hasRegisteredToken]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && firebaseUser && uid && !hasRegisteredToken) {
        void initFirebaseMessaging().then((token: string | null) => {
          if (token) {
            const platform = detectPlatformForFcm();
            void saveFcmToken(uid, token, platform);
            setCurrentFcmToken(token);
            setHasRegisteredToken(true);
          }
        });
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [firebaseUser, uid, hasRegisteredToken]);

  useEffect(() => {
    if (!firebaseUser || !uid) {
      setHasRegisteredToken(false);
      setCurrentFcmToken(null);
    }
  }, [firebaseUser, uid]);

  useEffect(() => {
    const detectCapabilities = async () => {
      const notificationsEnabled = APP_CONFIG.features.notifications;

      let notificationAccess = false;

      if (notificationsEnabled) {
        notificationAccess = await requestNotificationPermission();
        await logNotificationPermission(notificationAccess ? "granted" : "denied");
      }

      setCapabilities({ notifications: notificationAccess });
    };

    void detectCapabilities();
  }, []);

  return (
    <UserContext.Provider
      value={{
        user: firebaseUser,
        role,
        uid,
        capabilities,
        loading: loadingAppUser,
        logout,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = (): UserContextValue => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};
